import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAdminOfferPromo, createOfferPromo, updateOfferPromo } from '../../api/adminApi';
import { offerImageUrl } from '../../api/offerPromoApi';

const cyan = '#00e5ff';
const label = { color: '#aaa', fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' };
const input = {
  width: '100%',
  background: '#0a0a0a',
  border: '1px solid rgba(0,229,255,0.2)',
  color: '#fff',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 13,
  outline: 'none',
};

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const AdminOfferPromoForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', status: 'active', expiryDate: '' });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    getAdminOfferPromo(id)
      .then((res) => {
        const o = res.data.offer;
        setForm({ title: o.title, description: o.description || '', status: o.status, expiryDate: toDateInput(o.expiryDate) });
        setPreview(offerImageUrl(o.image));
      })
      .catch(() => {
        toast.error('Offer not found');
        navigate('/panel/offer-promos');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  useEffect(() => () => { if (preview.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2 MB or smaller');
      e.target.value = '';
      return;
    }
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !imageFile) return toast.error('Image is required');
    const fd = new FormData();
    fd.append('title', form.title.trim());
    fd.append('description', form.description.trim());
    fd.append('status', form.status);
    // End of selected day
    fd.append('expiryDate', new Date(`${form.expiryDate}T23:59:59`).toISOString());
    if (imageFile) fd.append('image', imageFile);

    setSaving(true);
    try {
      if (isEdit) await updateOfferPromo(id, fd);
      else await createOfferPromo(fd);
      toast.success(isEdit ? 'Offer updated' : 'Offer created');
      navigate('/panel/offer-promos');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: '#555', padding: 32 }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 640 }}>
      <button
        onClick={() => navigate('/panel/offer-promos')}
        style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}
      >
        ‹ Back to Offers Promo
      </button>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 24 }}>{isEdit ? 'Edit Offer' : 'Add Offer'}</h4>

      <form onSubmit={handleSubmit} style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: 24 }}>
        <div style={{ marginBottom: 18 }}>
          <label style={label}>Image {isEdit ? '(leave empty to keep current)' : '*'}</label>
          {preview && (
            <img src={preview} alt="Preview" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8, marginBottom: 10, display: 'block' }} />
          )}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImage} style={{ ...input, padding: 8 }} />
          <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>JPG, PNG, WEBP or GIF · max 2 MB</div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={label}>Title *</label>
          <input value={form.title} onChange={set('title')} maxLength={120} required style={input} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={label}>Description</label>
          <textarea value={form.description} onChange={set('description')} maxLength={1000} rows={4} style={{ ...input, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={label}>Status *</label>
            <select value={form.status} onChange={set('status')} style={input}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label style={label}>Expiry Date *</label>
            <input type="date" value={form.expiryDate} onChange={set('expiryDate')} required style={{ ...input, colorScheme: 'dark' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={saving}
            style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '10px 22px', fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : isEdit ? 'Update Offer' : 'Create Offer'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/panel/offer-promos')}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#aaa', borderRadius: 8, padding: '10px 22px', fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminOfferPromoForm;
